"use client";

import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Briefcase,
  FileText,
  Receipt,
  UserPlus,
  CheckCircle,
  Calendar,
  Send,
  Bell,
  Plus,
} from "lucide-react";
import { QuickJobModal } from "./QuickJobModal";
import { QuickQuoteModal } from "./QuickQuoteModal";
import { QuickInvoiceModal } from "./QuickInvoiceModal";
import { QuickClientModal } from "./QuickClientModal";
import { useTranslations } from "@/hooks/useTranslations";

interface QuickActionsMenuProps {
  trigger?: React.ReactNode;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
}

export function QuickActionsMenu({
  trigger,
  align = "end",
  side = "bottom",
}: QuickActionsMenuProps) {
  const t = useTranslations("quickActions.menu");
  const [isOpen, setIsOpen] = useState(false);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);

  const handleAction = (action: string) => {
    setIsOpen(false);
    switch (action) {
      case "new-job":
        setShowJobModal(true);
        break;
      case "new-quote":
        setShowQuoteModal(true);
        break;
      case "new-invoice":
        setShowInvoiceModal(true);
        break;
      case "new-client":
        setShowClientModal(true);
        break;
    }
  };

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          {trigger || (
            <Button size="sm" variant="default">
              <Plus className="h-4 w-4 mr-2" />
              {t("quickActions.title")}
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align={align} side={side}>
          <DropdownMenuLabel>{t("quickActions.title")}</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
              {t("quickActions.commands.jobs")}
            </DropdownMenuLabel>
            <DropdownMenuItem onClick={() => handleAction("new-job")}>
              <Briefcase className="mr-2 h-4 w-4" />
              <span>{t("quickActions.commands.newJob")}</span>
              <DropdownMenuShortcut>N J</DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem>
              <CheckCircle className="mr-2 h-4 w-4" />
              <span>{t("quickActions.menu.markJobComplete")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Calendar className="mr-2 h-4 w-4" />
              <span>{t("quickActions.menu.scheduleJob")}</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
              {t("quickActions.commands.clients")}
            </DropdownMenuLabel>
            <DropdownMenuItem onClick={() => handleAction("new-client")}>
              <UserPlus className="mr-2 h-4 w-4" />
              <span>{t("quickActions.menu.addClient")}</span>
              <DropdownMenuShortcut>N C</DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Send className="mr-2 h-4 w-4" />
              <span>{t("quickActions.menu.sendMessage")}</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
              {t("quickActions.commands.quotes")}
            </DropdownMenuLabel>
            <DropdownMenuItem onClick={() => handleAction("new-quote")}>
              <FileText className="mr-2 h-4 w-4" />
              <span>{t("quickActions.menu.createQuote")}</span>
              <DropdownMenuShortcut>N Q</DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Send className="mr-2 h-4 w-4" />
              <span>{t("quickActions.menu.sendQuote")}</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
              {t("quickActions.commands.invoices")}
            </DropdownMenuLabel>
            <DropdownMenuItem onClick={() => handleAction("new-invoice")}>
              <Receipt className="mr-2 h-4 w-4" />
              <span>{t("quickActions.menu.createInvoice")}</span>
              <DropdownMenuShortcut>N I</DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Bell className="mr-2 h-4 w-4" />
              <span>{t("quickActions.menu.sendPaymentReminder")}</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Quick Action Modals */}
      <QuickJobModal
        isOpen={showJobModal}
        onClose={() => setShowJobModal(false)}
      />
      <QuickQuoteModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
      />
      <QuickInvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
      />
      <QuickClientModal
        isOpen={showClientModal}
        onClose={() => setShowClientModal(false)}
      />
    </>
  );
}
